import { useNavigate } from 'react-router-dom';
import type { CSSProperties, ReactNode } from 'react';

import { useSidebar } from '@/contexts/SidebarContext';
import { Sidebar } from './Sidebar';
import { TopBar } from './TopBar';
import { MobileTabBar } from './MobileTabBar';
import { SeletorDaArea } from './SeletorDaArea';
// FEAT-V2-028: realtime notifications via Socket.io. Hook montado UMA vez
// no AppShell pra abrir 1 conexão por sessão.
import { useAuth } from '@/contexts/AuthContext';
// BLOCO K — a coroa do ranking aparece no sistema inteiro (27/09).
import { ProvedorDeCoroados, useCoroados } from '@/lib/crm/use-coroados';
import { useRealtimeNotifications } from '@/lib/realtime-client';
import { useToast } from '@/components/ui/Toast';

/**
 * AppShell — layout principal do app (todas as telas internas).
 *
 * Estrutura:
 *   Desktop (md+):
 *     ┌─────────────┬───────────────────────────┐
 *     │             │   TopBar (search + user)  │
 *     │  Sidebar    ├───────────────────────────┤
 *     │  fixa       │                           │
 *     │             │   Main (children)         │
 *     │             │                           │
 *     └─────────────┴───────────────────────────┘
 *
 *   Mobile (< md):
 *     ┌───────────────────────────────────────┐
 *     │   TopBar (logo + search compacto)     │
 *     ├───────────────────────────────────────┤
 *     │                                       │
 *     │   Main (children)                     │
 *     │                                       │
 *     ├───────────────────────────────────────┤
 *     │   MobileTabBar (5 itens)              │
 *     └───────────────────────────────────────┘
 *
 * Usado por HomePage, MenuPage, DashboardPage e demais telas internas.
 * Tela de Login NÃO usa AppShell (Login é full-screen sem chrome).
 */
export function AppShell({ children }: { children: ReactNode }) {
  const { collapsed } = useSidebar();
  const { user } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();

  // FEAT-V2-028: 1 conexão por sessão. Toast on receive (texto curto: title +
  // body em 1 linha). Polling 60s do TanStack Query continua como fallback
  // se WS desconectar. Navegação via payload.linkUrl é trabalho do
  // NotificationsBell (click no item da lista) — toast aqui é só awareness;
  // user clica no sino pra agir.
  // TD-V2-TOAST-CLICKABLE: melhorar Toast pra aceitar onClick → navega
  // direto pra payload.linkUrl. Por ora ignora linkUrl.
  void navigate;
  useRealtimeNotifications(user?.id ?? null, {
    onNewNotification: (payload) => {
      const msg = payload.body
        ? `${payload.title} — ${payload.body.slice(0, 100)}`
        : payload.title;
      toast.show(msg, { variant: 'info', durationMs: 4500 });
    },
  });

  /**
   * 🔴🔴 **QUEM ESTÁ DE COROA, uma vez para o sistema inteiro** — pedido do
   * Matheus em 27/09: *"quando um usuário tá com a coroa no ranking, em TODO o
   * sistema a coroa deverá aparecer nele também"*.
   *
   * ⚠️ **Mora na CASCA, e não em cada tela, por um motivo de aritmética:** um
   * Kanban tem dezenas de avatares. Se cada um perguntasse, seriam dezenas de
   * assinaturas para a MESMA resposta. ✅ Aqui é uma consulta por sessão, com
   * `staleTime` de cinco minutos — a coroa muda quando alguém fecha uma venda,
   * não a cada segundo.
   *
   * 🔴 **E a trava nasce LIGADA no mesmo commit.** Provedor escrito e não
   * montado é mecanismo órfão, e este projeto já pagou por isso duas vezes (o
   * interruptor do disparo em massa, #261, e a varredura de vocabulário).
   * ⚠️ Sem ele montado, o `userId` do Avatar não acharia ninguém e a coroa
   * simplesmente não apareceria — **falha silenciosa, que é a pior**.
   */
  const coroados = useCoroados();

  return (
    <ProvedorDeCoroados value={coroados}>
    <div
      className="min-h-screen bg-paper dark:bg-paper-dark"
      /* B-02 (HOTFIX-ROLAGEM): a altura da casca vira CONTRATO, não chute. As
         telas de altura cheia (Kanban) precisam descontar exatamente o que a
         casca ocupa; antes esse número era copiado no olho em cada tela
         (`100vh-14rem`, `100vh-3rem`) e qualquer divergência virava página
         rolando ou card cortado. Quem mudar a altura da TopBar/TabBar mexe
         AQUI e todas as telas acompanham.
         TopBar: h-14 (56px) + 1px de borda.
         Rodapé mobile: o que importa é o espaço RESERVADO no fluxo — o `pb-20`
         do <main> (80px), não a altura visual da barra de abas (61px). Descontar
         a barra em vez do padding deixava 19px sobrando e a página rolava. */
      style={
        {
          '--igm-topbar-h': '57px',
          '--igm-rodape-mobile-h': '80px',
          /**
           * C6-A #6 (08/08/2026) — o PISO da coluna do Kanban: quanto ela pode
           * encolher antes de o quadro parar e a PÁGINA passar a rolar.
           *
           * A conta é explícita de propósito, para o próximo que precisar
           * ajustar não ter de adivinhar de onde saiu o número:
           *   3 cards × 216px  = 648px   (card medido no funil, com o chip de
           *                               urgência e a linha de situação)
           * + 2 gaps  × 12px   =  24px   (o `space-y-3` entre cards)
           *                      -----
           *                       672px
           *
           * A esse valor soma-se o CROMO do quadro — cabeçalho da página, abas,
           * cabeçalho da coluna e a barra horizontal —, medido em 368px:
           *   672 + 368 = 1040px.
           *
           * O piso é 1020px, e não 1040: em 1920×1080 sobram 1023px (1080 menos
           * a TopBar), então 1040 fazia o Full HD rolar a página por DEZESSETE
           * pixels — feio e sem motivo. Ceder 20px custa ~0,1 card,
           * imperceptível, e devolve a tela cheia a quem tem monitor grande.
           * Decisão do Matheus, 08/08/2026.
           *
           * "~3 cards" é o pedido do Matheus. Menos que isso é a tela que ele
           * reclamou (1 card por coluna em 1366×768); muito mais empurraria a
           * página a rolar mesmo em monitor grande, sem necessidade.
           */
          '--igm-kanban-quadro-min': '1020px',
        } as CSSProperties
      }
    >
      <Sidebar />
      <MobileTabBar />

      {/* Conteúdo principal — padding-left reativo ao estado da sidebar (md+).
          Mobile (< md) não tem sidebar então pl-0. */}
      <div
        className={
          'transition-all duration-200 ease-out ' +
          (collapsed ? 'md:pl-16' : 'md:pl-64 lg:pl-72')
        }
      >
        <TopBar />
        {/* B-02 (HOTFIX-ROLAGEM): o `md:pb-8` somava 32px à altura de uma tela
            de altura cheia (Kanban) e fazia a PÁGINA rolar esses 32px — a
            segunda barra vertical que competia com a da coluna. No desktop o
            respiro do rodapé já vem do `py-10` do PageContainer; no mobile o
            `pb-20` continua, porque ali ele reserva o espaço da barra de abas. */}
        <main className="pb-20 md:pb-0">
          {/* MOBILE-COMPLETO (M2): "Manutenção: Corretiva ▾" no topo, só no
              celular (`md:hidden`) — no computador não ocupa nada. */}
          <SeletorDaArea />
          {children}
        </main>
      </div>
    </div>
    </ProvedorDeCoroados>
  );
}
